/**
 * Générateur — Traduire un problème, une succession d'opérations, en une seule
 * expression, avec ou sans parenthèses.
 * Attendu ID : 695 | Niveau : 5ème | Domaine : Opérations (cycle 4 rénové, cluster 3)
 *
 * Plan de cartes (validé par le responsable pédagogique) :
 *
 *   Carte « salaire » — Ex.1 Le salaire de l'ouvrière (ADAPTÉ, arbitrage
 *   utilisateur) : les 4,33 semaines/mois du doc rendaient le résultat non
 *   scorable (1 517,0155… → arrondi obligatoire, plusieurs écritures justes).
 *   Adaptation : volume horaire MENSUEL ENTIER (« 152 heures par mois ») →
 *   h × (s − r) exact au centime par construction. Structure de l'expression
 *   et pièges inchangés.
 *     Formats : qcm_unique (choisir l'expression), texte (effectuer).
 *     Misconceptions : retenue soustraite une seule fois (h×s − r), retenue
 *     soustraite des heures ((h − r) × s), retenue ajoutée (h×s + h×r).
 *
 *   Carte « somme_produits » — Ex.2 + Ex.4 fusionnés (même structure).
 *     Formats : qcm_unique (expression), texte_trou_saisie (calcul décomposé).
 *     Rotation 2 termes entiers / 3 termes décimaux. Les produits d'étapes
 *     sont FORCÉS DISTINCTS (arbitrage utilisateur : dans le doc 6×18 = 9×12
 *     = 108 — légal au scoring mais chaque trou doit tester SON produit).
 *     Misconceptions : produits croisés (n1×p2 + n2×p1), tout multiplier
 *     ((n1+n2) × (p1+p2)), tout additionner.
 *
 *   Carte « rebours » — Ex.5 Les livres de la classe (travail à rebours).
 *     Formats : qcm_unique (expression de la dépense connue n × (p1 + p2)),
 *     texte_trou_saisie guidé (dépense connue → reste → division).
 *     Construction à rebours : le prix du livre inconnu est tiré D'ABORD
 *     (valeur simple), le total s'en déduit → toutes les étapes exactes.
 */

import {
    randomChoice, randInt,
    makeQcm, makeSaisie, makeTexteTrou,
} from '../../../utils';

// ─── Constantes ──────────────────────────────────────────────
const ATTENDU_ID = 695;

const L_SAL  = 'Traduire et calculer un salaire net';
const L_SP   = 'Traduire une somme de produits';
const L_REB  = 'Traduire un problème à rebours (les livres)';

const opts = (label) => ({ niveau: '5eme', matiere: 'maths', attendu: ATTENDU_ID, label });

/** Centimes → écriture FR sans zéros finaux. */
const centToFr = (c) => {
    const ent = Math.floor(c / 100);
    const cents = c % 100;
    if (cents === 0) return String(ent);
    return `${ent},${String(cents).padStart(2, '0').replace(/0+$/, '')}`;
};

// Pas de dedupeChoix ici : les pièges d'expressions sont tous distincts PAR
// STRUCTURE (opérateurs/parenthèses différents sur les mêmes nombres).

// ═══════════════════════════════════════════════════════════
// Carte 1 — Le salaire net
// ═══════════════════════════════════════════════════════════

const CTX_SALAIRE = [
    { qui: 'Une ouvrière', pr: 'elle', poss: 'son' },
    { qui: 'Un technicien', pr: 'il', poss: 'son' },
    { qui: 'Une infirmière', pr: 'elle', poss: 'son' },
    { qui: 'Un magasinier', pr: 'il', poss: 'son' },
];

const tirerSalaire = () => {
    const h = randomChoice([140, 144, 148, 152, 156, 160]);
    const s = randInt(12, 16);                            // salaire horaire brut (entier)
    const rC = randomChoice([199, 249, 299, 349]);        // retenue horaire (centimes)
    const netC = s * 100 - rC;                            // net horaire (centimes)
    return { ctx: randomChoice(CTX_SALAIRE), h, s, rF: centToFr(rC), netC, totalC: h * netC };
};

const enonceSalaire = ({ ctx, h, s, rF }) =>
    `<p>${ctx.qui} travaille <strong>${h} heures par mois</strong>. ` +
    `Son salaire horaire est de <strong>${s} €</strong>. On retient sur ce salaire horaire ` +
    `<strong>${rF} €</strong> de cotisations sociales.</p>`;

const genSalaireQcm = () => {
    const t = tirerSalaire();
    const { h, s, rF } = t;
    const bonne = `${h} × (${s} − ${rF})`;
    const pieges = [
        `${h} × ${s} − ${rF}`,          // retenue soustraite une seule fois
        `(${h} − ${rF}) × ${s}`,        // retenue soustraite des heures
        `${h} × ${s} + ${h} × ${rF}`,   // retenue ajoutée
    ];
    const texte = enonceSalaire(t) +
        `<p>Quelle expression permet de calculer le <strong>salaire mensuel net</strong> ` +
        `(après retenue des cotisations) ?</p>`;
    return makeQcm([bonne, ...pieges], bonne, texte, opts(L_SAL));
};

const genSalaireSaisie = () => {
    const t = tirerSalaire();
    const texte = enonceSalaire(t) +
        '<p>Calcule le salaire mensuel net. Écris ta réponse en écriture décimale ' +
        '(ex. : 1521,52).</p>';
    return makeSaisie(centToFr(t.totalC), texte, { ...opts(L_SAL), suffixe: '€' });
};

// ═══════════════════════════════════════════════════════════
// Carte 2 — Somme de produits
// ═══════════════════════════════════════════════════════════

const CTX_SP2 = [
    { c1: 'sacs', o1: 'bouteilles en plastique', c2: 'sacs', o2: 'canettes en aluminium', quoi: 'déchets ramassés' },
    { c1: 'caisses', o1: 'pommes', c2: 'caisses', o2: 'poires', quoi: 'fruits récoltés' },
    { c1: 'boîtes', o1: 'crayons', c2: 'boîtes', o2: 'feutres', quoi: 'objets distribués' },
];

/** 2 termes entiers — produits FORCÉS distincts (chaque trou teste SON produit). */
const tirerSP2 = () => {
    for (let i = 0; i < 40; i++) {
        const n1 = randInt(4, 12), p1 = randInt(8, 25);
        const n2 = randInt(4, 12), p2 = randInt(8, 25);
        if (n1 === n2 || p1 === p2) continue;
        const P1 = n1 * p1, P2 = n2 * p2;
        if (P1 === P2) continue;
        const croise = n1 * p2 + n2 * p1;
        if (croise === P1 + P2) continue;               // le piège croisé doit être FAUX
        return { n1, p1, n2, p2, P1, P2, T: P1 + P2, croise, ctx: randomChoice(CTX_SP2) };
    }
    return { n1: 6, p1: 18, n2: 9, p2: 14, P1: 108, P2: 126, T: 234, croise: 6 * 14 + 9 * 18, ctx: CTX_SP2[0] };
};

/** 3 termes décimaux (zone humide) — volumes en centièmes, produits distincts. */
const tirerSP3 = () => {
    for (let i = 0; i < 40; i++) {
        const ns = [randInt(2, 6), randInt(2, 6), randInt(2, 6)];
        const vs = [randInt(105, 395), randInt(105, 395), randInt(105, 395)]
            .map(v => (v % 5 === 0 ? v + randomChoice([1, 2, 3]) : v));   // centièmes, pas ronds
        const Ps = ns.map((n, i2) => n * vs[i2]);
        if (new Set(Ps).size !== 3) continue;
        return { ns, vs, Ps, T: Ps[0] + Ps[1] + Ps[2] };
    }
    return { ns: [3, 5, 2], vs: [241, 176, 361], Ps: [723, 880, 722], T: 2325 };
};

const genSommeProduitsQcm = () => {
    if (randomChoice([true, false])) {
        const t = tirerSP2();
        const { n1, p1, n2, p2, ctx } = t;
        const bonne = `${n1} × ${p1} + ${n2} × ${p2}`;
        const pieges = [
            `${n1} × ${p2} + ${n2} × ${p1}`,            // produits croisés
            `(${n1} + ${n2}) × (${p1} + ${p2})`,        // tout multiplier
            `${n1} + ${p1} + ${n2} + ${p2}`,            // tout additionner
        ];
        const texte = `<p>Des bénévoles collectent <strong>${n1} ${ctx.c1}</strong> contenant chacun ` +
            `<strong>${p1} ${ctx.o1}</strong> et <strong>${n2} ${ctx.c2}</strong> contenant chacun ` +
            `<strong>${p2} ${ctx.o2}</strong>.</p>` +
            `<p>Quelle expression permet de calculer le nombre total de ${ctx.quoi} ?</p>`;
        return makeQcm([bonne, ...pieges], bonne, texte, opts(L_SP));
    }
    const t = tirerSP3();
    const [n1, n2, n3] = t.ns;
    const [v1, v2, v3] = t.vs.map(centToFr);
    const bonne = `${n1} × ${v1} + ${n2} × ${v2} + ${n3} × ${v3}`;
    const pieges = [
        `(${n1} + ${n2} + ${n3}) × (${v1} + ${v2} + ${v3})`,
        `${n1} × ${v2} + ${n2} × ${v3} + ${n3} × ${v1}`,     // produits croisés
        `${n1} + ${v1} + ${n2} + ${v2} + ${n3} + ${v3}`,
    ];
    const texte = '<p>Pour restaurer une zone humide : ' +
        `<strong>${n1} zones</strong> reçoivent chacune <strong>${v1} m³</strong> de terre, ` +
        `<strong>${n2} zones</strong> reçoivent chacune <strong>${v2} m³</strong> et ` +
        `<strong>${n3} zones</strong> reçoivent chacune <strong>${v3} m³</strong>.</p>` +
        '<p>Quelle expression permet de calculer le volume total de terre déplacée ?</p>';
    return makeQcm([bonne, ...pieges], bonne, texte, opts(L_SP));
};

const genSommeProduitsTrous = () => {
    if (randomChoice([true, false])) {
        const t = tirerSP2();
        const { n1, p1, n2, p2, P1, P2, T, ctx } = t;
        const texte = `<p>Des bénévoles collectent <strong>${n1} ${ctx.c1}</strong> contenant chacun ` +
            `<strong>${p1} ${ctx.o1}</strong> et <strong>${n2} ${ctx.c2}</strong> contenant chacun ` +
            `<strong>${p2} ${ctx.o2}</strong>. Combien de ${ctx.quoi} en tout ? Complète :</p>`;
        const phrase =
            `T = ${n1} × ${p1} + ${n2} × ${p2}\n` +
            `T = {{${P1}}} + {{${P2}}}\n` +
            `T = {{${T}}}`;
        return makeTexteTrou(phrase, texte, { ...opts(L_SP), mode: 'saisie' });
    }
    const t = tirerSP3();
    const [n1, n2, n3] = t.ns;
    const [v1, v2, v3] = t.vs.map(centToFr);
    const [P1, P2, P3] = t.Ps.map(centToFr);
    const texte = '<p>Pour restaurer une zone humide : ' +
        `<strong>${n1} zones</strong> reçoivent chacune <strong>${v1} m³</strong> de terre, ` +
        `<strong>${n2} zones</strong> reçoivent chacune <strong>${v2} m³</strong> et ` +
        `<strong>${n3} zones</strong> reçoivent chacune <strong>${v3} m³</strong>. ` +
        'Quel volume total ? Complète (écris les nombres sans zéro inutile, ex. : 12,5) :</p>';
    const phrase =
        `V = ${n1} × ${v1} + ${n2} × ${v2} + ${n3} × ${v3}\n` +
        `V = {{${P1}}} + {{${P2}}} + {{${P3}}}\n` +
        `V = {{${centToFr(t.T)}}}`;
    return makeTexteTrou(phrase, texte, { ...opts(L_SP), mode: 'saisie' });
};

// ═══════════════════════════════════════════════════════════
// Carte 3 — Travail à rebours (les livres)
// ═══════════════════════════════════════════════════════════

const CTX_REB = [
    { objets: 'livres', m1: 'de mathématiques', m2: 'de sciences', m3: 'de français' },
    { objets: 'manuels', m1: "d'histoire", m2: "d'anglais", m3: "d'espagnol" },
];

/** Le prix inconnu est tiré D'ABORD → toutes les étapes exactes à rebours. */
const tirerRebours = () => {
    const n = randomChoice([25, 26, 28, 29, 30]);
    const p1C = randInt(9, 14) * 100 + randomChoice([40, 60, 20, 80]);   // 2 déc « commerçantes »
    const p2 = randInt(18, 26);                                          // entier
    const pfC = randomChoice([2000, 2200, 2400, 2500, 2600, 2800]);      // prix inconnu (simple)
    const dC = n * (p1C + p2 * 100);                                     // dépense connue
    const rC = n * pfC;                                                  // reste
    return { n, p1C, p2, pfC, dC, rC, tC: dC + rC, ctx: randomChoice(CTX_REB) };
};

const enonceRebours = ({ n, p1C, p2, tC, ctx }) =>
    `<p>Dans une classe de <strong>${n} élèves</strong>, chacun reçoit un ${ctx.objets.slice(0, -1)} ` +
    `${ctx.m1} à <strong>${centToFr(p1C)} €</strong>, un ${ctx.objets.slice(0, -1)} ${ctx.m2} à ` +
    `<strong>${p2} €</strong> et un ${ctx.objets.slice(0, -1)} ${ctx.m3}. ` +
    `Le collège a dépensé en tout <strong>${centToFr(tC)} €</strong>.</p>`;

const genReboursQcm = () => {
    const t = tirerRebours();
    const { n, p1C, p2 } = t;
    const p1F = centToFr(p1C);
    const bonne = `${n} × (${p1F} + ${p2})`;
    const pieges = [
        `${n} × ${p1F} + ${p2}`,          // le prix sciences n'est compté qu'une fois
        `${n} + ${p1F} + ${p2}`,          // tout additionner
        `(${n} + ${p1F}) × ${p2}`,        // structure inversée
    ];
    const texte = enonceRebours(t) +
        `<p>Quelle expression donne la dépense pour les ${t.ctx.objets} ${t.ctx.m1} et ${t.ctx.m2} ?</p>`;
    return makeQcm([bonne, ...pieges], bonne, texte, opts(L_REB));
};

const genReboursTrous = () => {
    const t = tirerRebours();
    const { n, p1C, p2, pfC, dC, rC } = t;
    const texte = enonceRebours(t) +
        `<p>Quel est le prix d'un ${t.ctx.objets.slice(0, -1)} ${t.ctx.m3} ? ` +
        'Complète (écris les nombres sans zéro inutile, ex. : 12,5) :</p>';
    const phrase =
        `Dépense pour ${t.ctx.m1.replace(/^d[e']\s?/, '')} et ${t.ctx.m2.replace(/^d[e']\s?/, '')} : ` +
        `${n} × (${centToFr(p1C)} + ${p2}) = {{${centToFr(dC)}}} €\n` +
        `Reste : ${centToFr(t.tC)} − {{${centToFr(dC)}}} = {{${centToFr(rC)}}} €\n` +
        `Prix d'un ${t.ctx.objets.slice(0, -1)} : {{${centToFr(rC)}}} ÷ ${n} = {{${centToFr(pfC)}}} €`;
    return makeTexteTrou(phrase, texte, { ...opts(L_REB), mode: 'saisie' });
};

// ─── Registry & Export ───────────────────────────────────────

const GENERATORS = {
    salaire_qcm_unique:            genSalaireQcm,
    salaire_texte:                 genSalaireSaisie,
    somme_produits_qcm_unique:     genSommeProduitsQcm,
    somme_produits_texte_trou_saisie: genSommeProduitsTrous,
    rebours_qcm_unique:            genReboursQcm,
    rebours_texte_trou_saisie:     genReboursTrous,
};

export const CATEGORIES = [
    {
        id: 'salaire', label: L_SAL,
        formatsList: [
            { key: 'salaire_qcm_unique', label: 'QCM' },
            { key: 'salaire_texte',      label: 'Saisie' },
        ],
    },
    {
        id: 'somme_produits', label: L_SP,
        formatsList: [
            { key: 'somme_produits_qcm_unique',        label: 'QCM' },
            { key: 'somme_produits_texte_trou_saisie', label: 'Saisie trous' },
        ],
    },
    {
        id: 'rebours', label: L_REB,
        formatsList: [
            { key: 'rebours_qcm_unique',        label: 'QCM' },
            { key: 'rebours_texte_trou_saisie', label: 'Saisie trous' },
        ],
    },
];

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) {
        key = typeExercice;
    } else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter(
            (k) => k.startsWith(typeExercice) || k.includes(typeExercice),
        );
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else {
        key = randomChoice(Object.keys(GENERATORS));
    }
    const payload = GENERATORS[key]();
    if (!payload._meta.generatorKey) payload._meta.generatorKey = key;
    return payload;
};

export const _internals = {
    centToFr, tirerSalaire, tirerSP2, tirerSP3, tirerRebours,
};
